import React from 'react';
import { Box, Popover, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import SlideThumbnail from './SlideThumbnail';

import { SAMPLE_SLIDE, THEME_LIST, THEME_REGISTRY, type ThemeId } from '@/shared/theming/registry';

// ---------------------------------------------------------------------------
// SlideThemePicker
//
// Deck-wide theme picker (Phase 2 §7). Lists every built-in theme with a live,
// pixel-faithful mini-preview (decoration included) rendered through the shared
// SlideThumbnail path. The active theme is marked but stays selectable, so
// re-selecting it re-applies the current registry snapshot (§6.2 / §7.1).
// Recovery from a switch is via undo — no preview-confirm dialog.
// ---------------------------------------------------------------------------

const PREVIEW_WIDTH = 200;

interface SlideThemePickerProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  activeThemeId: string;
  onClose: () => void;
  onSelect: (themeId: ThemeId) => void;
}

const SlideThemePicker: React.FC<SlideThemePickerProps> = ({
  anchorEl,
  open,
  activeThemeId,
  onClose,
  onSelect,
}) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
  >
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {THEME_LIST.map((info) => {
        const isActive = info.themeId === activeThemeId;
        return (
          <Box
            key={info.themeId}
            onClick={() => onSelect(info.themeId)}
            sx={{
              cursor: 'pointer',
              p: 0.5,
              borderRadius: 1,
              outline: isActive ? '2px solid' : '1px solid',
              outlineColor: isActive ? 'primary.main' : 'divider',
              '&:hover': { outline: '2px solid', outlineColor: 'primary.main' },
            }}
          >
            <Box sx={{ position: 'relative', lineHeight: 0 }}>
              <SlideThumbnail
                slide={SAMPLE_SLIDE}
                theme={THEME_REGISTRY[info.themeId]}
                width={PREVIEW_WIDTH}
              />
              {isActive && (
                <CheckCircleIcon
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    fontSize: 18,
                    bgcolor: 'background.paper',
                    borderRadius: '50%',
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              {info.name}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Popover>
);

export default SlideThemePicker;
